import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { CameraCapture } from "@/components/guests/CameraCapture";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { loadFaceModels, extractEmbeddings } from "@/lib/face";

export default function CheckIn() {
  const { eventSlug } = useParams<{ eventSlug: string }>();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [step, setStep] = useState<"form" | "selfie" | "done">("form");
  const [selfieBlob, setSelfieBlob] = useState<Blob | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [createdGuestId, setCreatedGuestId] = useState<string | null>(null);

  // Lazy-load face models pas user masuk halaman check-in
  useEffect(() => {
    loadFaceModels().catch((err) => console.warn("Face models preload warning:", err));
  }, []);

  const event = useQuery(
    api.events.getBySlug,
    eventSlug ? { slug: eventSlug } : "skip"
  );

  const checkin = useMutation(api.guests.checkin);
  const generateUploadUrl = useMutation(api.guests.generateUploadUrl);

  const handleSubmit = async () => {
    if (!event || !selfieBlob) return;

    setLoading(true);
    setErrorMsg("");

    try {
      // 1. Ekstrak face embedding dari foto selfie di browser
      let embedding: number[] | undefined = undefined;
      try {
        const embeddings = await extractEmbeddings(selfieBlob);
        if (embeddings.length > 0) {
          embedding = embeddings[0]; // Ambil wajah pertama
        }
      } catch (faceErr) {
        console.warn("Ekstraksi wajah gagal/di-skip:", faceErr);
        // Tetap lanjutkan check-in meski face match offline
      }

      // 2. Upload file ke Convex Storage
      const uploadUrl = await generateUploadUrl({});
      const uploadRes = await fetch(uploadUrl, {
        method: "POST",
        headers: { "Content-Type": selfieBlob.type },
        body: selfieBlob,
      });

      if (!uploadRes.ok) {
        const detail = await uploadRes.text().catch(() => "");
        throw new Error(`Upload gagal (${uploadRes.status}): ${detail || uploadRes.statusText}`);
      }

      const { storageId } = await uploadRes.json();

      // 3. Insert ke tabel guests dengan embedding
      const guestId = await checkin({
        eventId: event._id,
        name: name.trim(),
        phone: phone.replace(/\D/g, ""),
        selfieStorageId: storageId,
        embedding: embedding,
      });

      setCreatedGuestId(guestId);
      setStep("done");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Gagal check-in";
      setErrorMsg(message);
    } finally {
      setLoading(false);
    }
  };

  if (event === undefined) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="flex flex-col items-center gap-2 text-slate-500 text-sm">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          <span>Memuat info acara...</span>
        </div>
      </div>
    );
  }

  if (event === null) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center p-6">
          <h2 className="text-lg font-bold text-slate-900 mb-1">Event Tidak Ditemukan</h2>
          <p className="text-xs text-slate-500">
            Slug <span className="font-mono font-semibold">{eventSlug}</span> tidak terdaftar. Periksa QR code Anda.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <span className="text-2xl">📸</span>
          <h1 className="text-xl font-bold text-slate-900 mt-1">
            {event ? event.name : "Memuat Event..."}
          </h1>
          <p className="text-xs text-slate-500">{event?.location}</p>
        </div>

        <Card>
          {step === "form" && (
            <>
              <CardHeader>
                <CardTitle className="text-base">1. Isi Data Diri</CardTitle>
                <CardDescription>Masukkan nama & nomor WhatsApp untuk menerima foto</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-slate-700 block mb-1">Nama Lengkap *</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Budi Santoso"
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[40px]"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-700 block mb-1">Nomor WhatsApp *</label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="081234567890"
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[40px]"
                  />
                </div>
                <Button
                  disabled={!name.trim() || !phone.trim() || !event}
                  className="w-full"
                  onClick={() => setStep("selfie")}
                >
                  Lanjut ke Selfie &rarr;
                </Button>
              </CardContent>
            </>
          )}

          {step === "selfie" && (
            <>
              <CardHeader>
                <CardTitle className="text-base">2. Ambil Foto Selfie</CardTitle>
                <CardDescription>Foto ini digunakan untuk mencocokkan wajah di album event</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <CameraCapture onCapture={(blob) => setSelfieBlob(blob)} />

                {errorMsg && (
                  <div className="p-3 bg-red-50 text-red-700 rounded-lg text-xs flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <span>{errorMsg}</span>
                  </div>
                )}

                <Button
                  className="w-full"
                  disabled={!selfieBlob || loading}
                  onClick={handleSubmit}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Memindai Wajah & Check-in...
                    </>
                  ) : (
                    "Submit Check-in"
                  )}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setStep("form")}>
                  &larr; Kembali
                </Button>
              </CardContent>
            </>
          )}

          {step === "done" && (
            <>
              <CardHeader className="text-center">
                <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-2">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <CardTitle className="text-lg">Check-in Berhasil!</CardTitle>
                <CardDescription>
                  Terima kasih <span className="font-semibold text-slate-900">{name}</span> sudah hadir.
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center space-y-4">
                <p className="text-xs text-slate-500">
                  Foto yang mencakup wajahmu akan dikirim otomatis ke WhatsApp <span className="font-mono text-slate-700">{phone}</span> saat fotografer mengunggah album acara.
                </p>

                {createdGuestId && (
                  <Button
                    className="w-full text-xs"
                    onClick={() => navigate(`/gallery/${createdGuestId}`)}
                  >
                    Buka Galeri Foto Saya &rarr;
                  </Button>
                )}

                <Button variant="outline" size="sm" onClick={() => { setStep("form"); setName(""); setPhone(""); setSelfieBlob(null); setErrorMsg(""); }}>
                  Check-in Tamu Lain
                </Button>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}